import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { socialMediaService } from '../../../shared/services/socialMediaService';
import { supabase } from '../../../shared/lib/supabase';
import Toast from '../../../shared/components/ui/Toast';

interface ToastState {
  isVisible: boolean;
  type: 'success' | 'error' | 'info';
  message: string;
}

const SocialMediaSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { currentOrganization } = useOrg();
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const orgId = currentOrganization?.id || orgIdFromPath || '';

  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [orgIntegrations, setOrgIntegrations] = useState<any[]>([]);
  const [parentIntegration, setParentIntegration] = useState<any>(null);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    type: 'info',
    message: '',
  });

  // Check if user is org admin (not just global admin)
  useEffect(() => {
    const checkOrgAdmin = async () => {
      if (!user || !orgId) {
        setIsOrgAdmin(false);
        return;
      }

      try {
        const { data: userOrg, error } = await supabase
          .from('user_organisations')
          .select('role')
          .eq('user_id', user.id)
          .eq('organisation_id', orgId)
          .single();

        if (!error && userOrg) {
          const orgAdmin = userOrg.role === 'admin' || userOrg.role === 'super_admin';
          setIsOrgAdmin(orgAdmin || isAdmin);
        } else {
          setIsOrgAdmin(isAdmin);
        }
      } catch (error) {
        console.error('Error checking org admin:', error);
        setIsOrgAdmin(isAdmin);
      }
    };

    checkOrgAdmin();
  }, [user, orgId, isAdmin]);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data: userData, error } = await supabase
          .from('users')
          .select('global_role')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking admin status:', error);
          setIsLoading(false);
          return;
        }

        const admin = userData?.global_role === 'super_admin' || userData?.global_role === 'admin';
        setIsAdmin(admin);
        setIsLoading(false);

        if (admin) {
          loadIntegrations();
        } else if (orgId) {
          // Non-admin users can still view org integrations
          loadOrgIntegrations();
        }
      } catch (error) {
        console.error('Error in checkAdminStatus:', error);
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  // Load org integrations
  const loadOrgIntegrations = async () => {
    if (!orgId) return;
    try {
      const result = await socialMediaService.getOrgIntegrations(orgId, 'youtube');
      if (result.success && result.data) {
        setOrgIntegrations(result.data);
      } else {
        setOrgIntegrations([]);
      }
    } catch (error) {
      console.error('Error loading org integrations:', error);
      setOrgIntegrations([]);
    }
  };

  // Load all integrations (org + parent)
  const loadIntegrations = async () => {
    try {
      // Load org integrations
      if (orgId) {
        await loadOrgIntegrations();
      }

      // Load parent integration (for admin fallback view)
      const parentResult = await socialMediaService.getParentIntegration('youtube');
      if (parentResult.success && parentResult.data) {
        setParentIntegration(parentResult.data);
      } else {
        setParentIntegration(null);
      }
    } catch (error) {
      console.error('Error loading integrations:', error);
    }
  };

  // Handle OAuth callback (check if we're returning from OAuth)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const channelName = urlParams.get('channelName');

    if (success === 'true' || error) {
      if (error) {
        setToast({
          isVisible: true,
          type: 'error',
          message: `OAuth error: ${error}`,
        });
      } else {
        // Success - reload integrations
        if (isAdmin) {
          loadIntegrations();
        } else {
          loadOrgIntegrations();
        }
        setToast({
          isVisible: true,
          type: 'success',
          message: channelName 
            ? `YouTube account "${decodeURIComponent(channelName)}" connected successfully!`
            : 'YouTube account connected successfully!',
        });
      }
      // Clean URL
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location]);

  // Initiate OAuth flow for org account
  const handleConnectYouTube = async (isParentLevel: boolean = false) => {
    if (!user) {
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Please log in to connect YouTube account',
      });
      return;
    }

    setIsConnecting(true);

    try {
      // Get session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      // Get OAuth URL from backend (edge function will provide it securely)
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
      const redirectUri = encodeURIComponent(`${window.location.origin}/o/${orgId}/capture/social-media-settings`);
      
      // Determine state: "parent" for parent-level, "org:orgId" for org-level
      const state = isParentLevel ? 'parent' : `org:${orgId}`;
      
      // Call edge function to get OAuth URL
      const response = await fetch(`${supabaseUrl}/functions/v1/youtube-oauth-callback?action=initiate&redirect_uri=${redirectUri}&state=${state}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initiate OAuth');
      }

      const data = await response.json();
      if (data.oauthUrl) {
        window.location.href = data.oauthUrl;
      } else {
        throw new Error('No OAuth URL returned from server');
      }
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to initiate OAuth flow',
      });
      setIsConnecting(false);
    }
  };

  // Delete org integration
  const handleDeleteIntegration = async (integrationId: string) => {
    if (!orgId) return;

    if (!window.confirm('Are you sure you want to disconnect this YouTube account? This will not delete any existing uploads.')) {
      return;
    }

    try {
      const result = await socialMediaService.deleteOrgIntegration(integrationId, orgId);
      if (result.success) {
        setToast({
          isVisible: true,
          type: 'success',
          message: 'YouTube account disconnected successfully',
        });
        loadOrgIntegrations();
      } else {
        setToast({
          isVisible: true,
          type: 'error',
          message: result.error || 'Failed to disconnect account',
        });
      }
    } catch (error) {
      console.error('Error deleting integration:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to disconnect account',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!isAdmin && !isOrgAdmin) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">Admin access required to manage social media integrations.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Note: Access tokens expire in 1 hour (standard OAuth behavior)
  // Refresh tokens are long-lived and auto-refresh access tokens
  // No need to show expiry status to users as it's handled automatically

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Social Media Settings</h1>
        <p className="text-gray-600 mt-1">Manage YouTube and other social media platform integrations</p>
      </div>

      {/* Platform Integrations Grid */}
      <div className="space-y-6">
        {/* YouTube - Enabled Platform */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800">YouTube</h2>
                <p className="text-sm text-gray-600">Connect your YouTube channel</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
              Available
            </span>
          </div>

          {/* Org Accounts Section - Primary */}
          {orgIntegrations.length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-3">
                {orgIntegrations.map((integration) => (
                  <div
                    key={integration.id}
                    className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-800">
                          {integration.display_name || integration.platform_account_name}
                        </h3>
                        <p className="text-sm text-gray-600">{integration.platform_account_name}</p>
                      </div>
                      {integration.is_active && integration.platform_account_id !== 'placeholder' ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                          Connected
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                          Not Connected
                        </span>
                      )}
                    </div>

                    {integration.is_active && integration.platform_account_id !== 'placeholder' ? (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <label className="text-xs font-medium text-gray-600">Channel ID</label>
                            <p className="text-gray-900 mt-1 font-mono text-xs break-all">
                              {integration.platform_account_id}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600">Status</label>
                            <p className="text-green-600 text-xs mt-1">✓ Active</p>
                          </div>
                        </div>
                        <div className="flex gap-2 pt-3 border-t border-blue-200">
                          <button
                            onClick={() => handleConnectYouTube(false)}
                            disabled={isConnecting}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isConnecting ? 'Connecting...' : 'Reconnect'}
                          </button>
                          <button
                            onClick={() => handleDeleteIntegration(integration.id)}
                            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                          >
                            Disconnect
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="pt-3 border-t border-blue-200">
                        <button
                          onClick={() => handleConnectYouTube(false)}
                          disabled={isConnecting}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isConnecting ? 'Connecting...' : 'Connect Account'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleConnectYouTube(false)}
                  disabled={true}
                  className="px-4 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed opacity-60"
                  title="Multiple accounts per organization temporarily disabled"
                >
                  + Connect Another YouTube Account
                </button>
              </div>
            </div>
          ) : (
            /* No org accounts - show connect button prominently */
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              <div className="mb-4">
                <svg className="w-12 h-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Connect Your YouTube Account</h3>
              <p className="text-sm text-gray-600 mb-4">
                Connect your organization's YouTube channel to upload videos directly from Kridah.
              </p>
              <button
                onClick={() => handleConnectYouTube(false)}
                disabled={isConnecting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isConnecting ? 'Connecting...' : 'Connect YouTube Account'}
              </button>
            </div>
          )}

          {/* Parent Account - Smaller, Secondary Section */}
          {parentIntegration && parentIntegration.is_active && parentIntegration.platform_account_id !== 'placeholder' && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <span className="text-xs font-medium text-gray-500 mr-2">Fallback Account:</span>
                      <span className="text-xs text-gray-600">{parentIntegration.platform_account_name}</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Kridah Labs parent account (used when no org account is connected)
                    </p>
                  </div>
                  <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded">
                    Active
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Other Platforms - Disabled */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Coming Soon</h2>
          <p className="text-sm text-gray-600 mb-4">
            We're working on adding support for more social media platforms. Stay tuned!
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {/* Instagram */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 opacity-60">
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">Instagram</p>
                <p className="text-xs text-gray-500 mt-1">Coming soon</p>
              </div>
            </div>

            {/* TikTok */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 opacity-60">
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">TikTok</p>
                <p className="text-xs text-gray-500 mt-1">Coming soon</p>
              </div>
            </div>

            {/* Facebook */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 opacity-60">
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">Facebook</p>
                <p className="text-xs text-gray-500 mt-1">Coming soon</p>
              </div>
            </div>

            {/* Twitter/X */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 opacity-60">
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">Twitter/X</p>
                <p className="text-xs text-gray-500 mt-1">Coming soon</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={() => setToast({ ...toast, isVisible: false })}
        />
      )}
    </div>
  );
};

export default SocialMediaSettingsPage;

